import { useEffect, useState } from "react";
import { Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { modeLabel } from "../../lib/modes";

const API = "/api";

// Shown inside delete confirmations: which drills use the question(s) and what the cascade will do to them.
export const DrillUsageNotice = ({ questionIds }) => {
  const [usage, setUsage] = useState(null);
  const key = JSON.stringify(questionIds);

  useEffect(() => {
    let alive = true;
    setUsage(null);
    fetch(`${API}/admin/drills/usage`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question_ids: JSON.parse(key) }),
    })
      .then((r) => (r.ok ? r.json() : { drills: [] }))
      .then((d) => { if (alive) setUsage(d.drills || []); })
      .catch(() => { if (alive) setUsage([]); });
    return () => { alive = false; };
  }, [key]);

  if (usage === null) {
    return <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="drill-usage-loading"><Loader2 className="w-3 h-3 animate-spin" /> Checking drills…</p>;
  }
  if (!usage.length) {
    return <p className="text-xs text-muted-foreground" data-testid="drill-usage-none">Not used in any drill.</p>;
  }
  return (
    <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/10 p-3 text-xs space-y-1.5" data-testid="drill-usage-notice">
      <p className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
        <ListChecks className="w-3.5 h-3.5" /> Used in {usage.length} drill{usage.length === 1 ? "" : "s"} — it will be removed from them and student progress re-counted:
      </p>
      <ul className="space-y-1 pl-5 list-disc">
        {usage.map((d) => (
          <li key={d.drill_id} data-testid={`drill-usage-${d.drill_id}`}>
            <span className="font-medium">{d.title}</span> <span className="text-muted-foreground">({modeLabel(d.mode)} · {d.question_count} question{d.question_count === 1 ? "" : "s"})</span>
            {" — "}
            {d.will_be_deleted
              ? <strong className="text-red-600 dark:text-red-400">no questions left, so the drill{d.assigned_classes ? ` and its ${d.assigned_classes} class assignment${d.assigned_classes === 1 ? "" : "s"}` : ""} will be deleted</strong>
              : <>{d.remaining} question{d.remaining === 1 ? "" : "s"} remain{d.assigned_classes ? ` · assigned to ${d.assigned_classes} class${d.assigned_classes === 1 ? "" : "es"}` : ""}</>}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Toasts summarising the cascade report returned by the delete endpoints.
export const toastDrillReport = (report) => {
  if (!report) return;
  const updated = report.drills_updated || [];
  const deleted = report.drills_deleted || [];
  if (updated.length) {
    toast.info(`Removed from ${updated.length} drill${updated.length === 1 ? "" : "s"}: ${updated.map((d) => `${d.title} (${d.remaining} left)`).join(", ")}${report.progress_fixed ? ` · ${report.progress_fixed} student progress record${report.progress_fixed === 1 ? "" : "s"} re-counted` : ""}`, { duration: 8000 });
  }
  if (deleted.length) {
    toast.warning(`Deleted ${deleted.length} empty drill${deleted.length === 1 ? "" : "s"}: ${deleted.map((d) => `${d.title}${d.classes_unassigned ? ` (unassigned from ${d.classes_unassigned} class${d.classes_unassigned === 1 ? "" : "es"})` : ""}`).join(", ")}`, { duration: 10000 });
  }
};
